/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\pages\Admin\GestionFeedbacks.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
 * Page Admin - Gestion des Feedbacks Bêta
 * Permet de consulter, filtrer et gérer tous les feedbacks reçus
 */
import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../services/httpClient';

interface Feedback {
  id: string;
  timestamp: string;
  type: 'bug' | 'improvement' | 'question';
  title: string;
  description: string;
  email: string;
  page: string;
  userAgent: string;
  status: 'nouveau' | 'en_cours' | 'résolu' | 'fermé';
  updated_at?: string;
  devisId?: string;
  devisData?: any;
}

const GestionFeedbacks: React.FC = () => {
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('tous');
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);

  // Charger les feedbacks
  useEffect(() => {
    loadFeedbacks();
    // Recharger toutes les 30 secondes
    const interval = setInterval(loadFeedbacks, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadFeedbacks = async () => {
    try {
      const data = await apiFetch<any>('/feedback/list');
      
      if (data.success) {
        setFeedbacks(data.feedbacks);
      } else {
        setError('Erreur lors du chargement des feedbacks');
      }
    } catch (err) {
      setError('Impossible de charger les feedbacks');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (feedbackId: string, newStatus: string) => {
    try {
      await apiFetch(`/feedback/${feedbackId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus })
      });

      loadFeedbacks(); // Recharger la liste
      if (selectedFeedback && selectedFeedback.id === feedbackId) {
        setSelectedFeedback({ ...selectedFeedback, status: newStatus as any });
      }
    } catch (err) {
      console.error('Erreur mise ? jour statut:', err);
    }
  };

  // Filtrer les feedbacks
  const feedbacksFiltres = feedbacks.filter(f => {
    if (filter === 'tous') return true;
    if (filter === 'nouveau') return f.status === 'nouveau';
    if (filter === f.type) return true;
    return false;
  });

  // Émoji et couleurs selon le type
  const getTypeInfo = (type: string) => {
    switch (type) {
      case 'bug':
        return { emoji: '🐛', color: 'bg-red-100 text-red-800', label: 'Bug' };
      case 'improvement':
        return { emoji: '💡', color: 'bg-blue-100 text-blue-800', label: 'Idée' };
      case 'question':
        return { emoji: '❓', color: 'bg-green-100 text-green-800', label: 'Question' };
      default:
        return { emoji: '📝', color: 'bg-gray-100 text-gray-800', label: type };
    }
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'nouveau':
        return { color: 'bg-yellow-100 text-yellow-800', label: '🆕 Nouveau' };
      case 'en_cours':
        return { color: 'bg-blue-100 text-blue-800', label: '⚙️ En cours' };
      case 'résolu':
        return { color: 'bg-green-100 text-green-800', label: '✅ Résolu' };
      case 'fermé':
        return { color: 'bg-gray-100 text-gray-800', label: '🔒 Fermé' };
      default:
        return { color: 'bg-gray-100 text-gray-800', label: status };
    }
  };

  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const countByStatus = (status: string) => feedbacks.filter(f => f.status === status).length;
  const countByType = (type: string) => feedbacks.filter(f => f.type === type).length;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Feedbacks Bêta</h1>
          <p className="text-sm text-gray-500 mt-1">
            {feedbacks.length} feedback{feedbacks.length > 1 ? 's' : ''} reçu{feedbacks.length > 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={loadFeedbacks}
          className="btn-secondary flex items-center"
        >
          <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Actualiser
        </button>
      </div>

      {/* Statistiques rapides */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="text-sm text-gray-500">Nouveaux</div>
          <div className="text-2xl font-bold text-yellow-600">{countByStatus('nouveau')}</div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-500">Bugs</div>
          <div className="text-2xl font-bold text-red-600">{countByType('bug')}</div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-500">Idées</div>
          <div className="text-2xl font-bold text-blue-600">{countByType('improvement')}</div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-500">Questions</div>
          <div className="text-2xl font-bold text-green-600">{countByType('question')}</div>
        </div>
      </div>

      {/* Filtres */}
      <div className="card">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilter('tous')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filter === 'tous'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Tous ({feedbacks.length})
          </button>
          <button
            onClick={() => setFilter('nouveau')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filter === 'nouveau'
                ? 'bg-yellow-600 text-white'
                : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
            }`}
          >
            🆕 Nouveaux ({countByStatus('nouveau')})
          </button>
          <button
            onClick={() => setFilter('bug')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filter === 'bug'
                ? 'bg-red-600 text-white'
                : 'bg-red-100 text-red-700 hover:bg-red-200'
            }`}
          >
            🐛 Bugs ({countByType('bug')})
          </button>
          <button
            onClick={() => setFilter('improvement')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filter === 'improvement'
                ? 'bg-blue-600 text-white'
                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            }`}
          >
            💡 Idées ({countByType('improvement')})
          </button>
          <button
            onClick={() => setFilter('question')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filter === 'question'
                ? 'bg-green-600 text-white'
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
          >
            ❓ Questions ({countByType('question')})
          </button>
        </div>
      </div>

      {/* Liste des feedbacks */}
      <div className="card">
        {feedbacksFiltres.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Aucun feedback à afficher</p>
          </div>
        ) : (
          <div className="space-y-4">
            {feedbacksFiltres.map((feedback) => {
              const typeInfo = getTypeInfo(feedback.type);
              const statusInfo = getStatusInfo(feedback.status);
              
              return (
                <div
                  key={feedback.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors cursor-pointer"
                  onClick={() => setSelectedFeedback(feedback)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${typeInfo.color}`}>
                          {typeInfo.emoji} {typeInfo.label}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(feedback.timestamp)}
                        </span>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900">{feedback.title}</h3>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{feedback.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        {feedback.email !== 'Non fourni' && (
                          <span>📧 {feedback.email}</span>
                        )}
                        <span>📄 {feedback.page}</span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal de détail */}
      {selectedFeedback && (
        <div className="fixed inset-0 z-50 overflow-y-auto" onClick={() => setSelectedFeedback(null)}>
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75"></div>
            <div
              className="relative bg-white rounded-lg max-w-2xl w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-semibold text-gray-900">{selectedFeedback.title}</h2>
                <button
                  onClick={() => setSelectedFeedback(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getTypeInfo(selectedFeedback.type).color} mr-2`}>
                    {getTypeInfo(selectedFeedback.type).emoji} {getTypeInfo(selectedFeedback.type).label}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusInfo(selectedFeedback.status).color}`}>
                    {getStatusInfo(selectedFeedback.status).label}
                  </span>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Description :</label>
                  <p className="mt-1 text-gray-600 whitespace-pre-wrap">{selectedFeedback.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Email :</label>
                    <p className="mt-1 text-gray-600">{selectedFeedback.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Date :</label>
                    <p className="mt-1 text-gray-600">{formatDate(selectedFeedback.timestamp)}</p>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Page concernée :</label>
                  <p className="mt-1 text-sm text-gray-600 font-mono bg-gray-50 p-2 rounded">{selectedFeedback.page}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Navigateur :</label>
                  <p className="mt-1 text-xs text-gray-500 font-mono bg-gray-50 p-2 rounded break-all">
                    {selectedFeedback.userAgent}
                  </p>
                </div>

                {/* Données du devis si présentes */}
                {selectedFeedback.devisId && (
                  <div className="border-t pt-4">
                    <label className="text-sm font-medium text-gray-700 block mb-2">📋 Devis concerné :</label>
                    <div className="bg-blue-50 p-3 rounded space-y-3">
                      <div className="flex justify-between items-center">
                        <p className="text-sm"><strong>ID:</strong> {selectedFeedback.devisId}</p>
                        <a
                          href={`/devis/${selectedFeedback.devisId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-primary text-xs py-1 px-3"
                          onClick={(e) => e.stopPropagation()}
                        >
                          🔗 Ouvrir le devis
                        </a>
                      </div>
                      {selectedFeedback.devisData ? (
                        <>
                          <p className="text-sm"><strong>Référence:</strong> {selectedFeedback.devisData.reference}</p>
                          <p className="text-sm"><strong>Client:</strong> {selectedFeedback.devisData.client?.nom || 'N/A'}</p>
                          <p className="text-sm"><strong>Montant HT:</strong> {selectedFeedback.devisData.montant_ht}€</p>
                          <p className="text-sm"><strong>Statut:</strong> {selectedFeedback.devisData.statut}</p>
                          
                          {/* Bouton pour afficher les données JSON */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const detailsElement = document.getElementById(`devis-details-${selectedFeedback.id}`);
                              if (detailsElement) {
                                detailsElement.classList.toggle('hidden');
                              }
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium underline"
                          >
                            👁️ Afficher/Masquer les données techniques complètes
                          </button>
                          
                          <div id={`devis-details-${selectedFeedback.id}`} className="hidden">
                            <pre className="text-xs bg-white p-2 rounded border overflow-auto max-h-60">
                              {JSON.stringify(selectedFeedback.devisData, null, 2)}
                            </pre>
                          </div>
                        </>
                      ) : (
                        <div className="text-sm text-orange-600 bg-orange-50 p-2 rounded">
                          ⚠️ Données du devis non disponibles (seulement l'ID a été sauvegardé)
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Changer le statut :</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateStatus(selectedFeedback.id, 'nouveau')}
                      className="btn-secondary text-sm"
                    >
                      Nouveau
                    </button>
                    <button
                      onClick={() => updateStatus(selectedFeedback.id, 'en_cours')}
                      className="btn-secondary text-sm"
                    >
                      En cours
                    </button>
                    <button
                      onClick={() => updateStatus(selectedFeedback.id, 'résolu')}
                      className="btn-primary text-sm"
                    >
                      Résolu
                    </button>
                    <button
                      onClick={() => updateStatus(selectedFeedback.id, 'fermé')}
                      className="btn-secondary text-sm"
                    >
                      Fermé
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionFeedbacks;
